export interface SkyAnalysis {
  /** Average colour of the sky just above the horizon, 0..1. */
  hazeColor: [number, number, number];
  /** Light azimuth in degrees, camera frame: 0 ahead (backlit), 90 right, 180 behind the viewer, 270 left. */
  lightAngle: number;
  /** 0 cool daylight .. 1 low warm sun. */
  lightWarmth: number;
}

export interface Photo extends SkyAnalysis {
  id: string;
  tex: WebGLTexture;
  width: number;
  height: number;
  /** Kept so the sky can be re-analysed for a new horizon without a reload. */
  img: HTMLImageElement;
}

export async function loadPhoto(gl: WebGL2RenderingContext, id: string, url: string, horizon: number): Promise<Photo> {
  const img = await loadImage(url);
  const tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB8, gl.RGB, gl.UNSIGNED_BYTE, img);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.generateMipmap(gl.TEXTURE_2D);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return { id, tex, width: img.naturalWidth, height: img.naturalHeight, img, ...analyzeSky(img, horizon) };
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`could not load ${url}`));
    img.src = url;
  });
}

/**
 * Reads the band of sky 2 %..14 % of the height above the horizon at 64×64.
 * Haze colour is its mean. Light direction comes from where the band is
 * brightest left to right; warmth from how much redder than blue it is.
 */
export function analyzeSky(img: CanvasImageSource, horizon: number): SkyAnalysis {
  const W = 64;
  const H = 64;
  const fallback: SkyAnalysis = { hazeColor: [0.7, 0.7, 0.75], lightAngle: 0, lightWarmth: 0.5 };
  const cv = document.createElement('canvas');
  cv.width = W;
  cv.height = H;
  const ctx = cv.getContext('2d', { willReadFrequently: true });
  if (!ctx) return fallback;
  ctx.drawImage(img, 0, 0, W, H);
  const y0 = Math.max(0, Math.floor((horizon - 0.14) * H));
  const y1 = Math.min(H, Math.max(y0 + 1, Math.floor((horizon - 0.02) * H)));
  const data = ctx.getImageData(0, y0, W, y1 - y0).data;
  let r = 0;
  let g = 0;
  let b = 0;
  const colLum = new Float64Array(W);
  const rows = y1 - y0;
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < W; x++) {
      const i = (y * W + x) * 4;
      const pr = data[i]!;
      const pg = data[i + 1]!;
      const pb = data[i + 2]!;
      r += pr;
      g += pg;
      b += pb;
      colLum[x]! += 0.2126 * pr + 0.7152 * pg + 0.0722 * pb;
    }
  }
  const n = rows * W;
  if (n === 0) return fallback;
  const mean: [number, number, number] = [r / n / 255, g / n / 255, b / n / 255];

  // Brightness centroid across columns, weighted toward the bright end so a
  // sun near one edge pulls harder than a uniformly bright sky.
  let lumMin = Infinity;
  let lumMax = -Infinity;
  for (let x = 0; x < W; x++) {
    lumMin = Math.min(lumMin, colLum[x]!);
    lumMax = Math.max(lumMax, colLum[x]!);
  }
  let cx = 0.5;
  if (lumMax - lumMin > 1e-6) {
    let wsum = 0;
    let xsum = 0;
    for (let x = 0; x < W; x++) {
      const w = Math.pow((colLum[x]! - lumMin) / (lumMax - lumMin), 3);
      wsum += w;
      xsum += w * (x + 0.5) / W;
    }
    if (wsum > 0) cx = xsum / wsum;
  }
  // A flat band is an overcast sky: soft light from behind the viewer, cool.
  const ratio = lumMin > 1e-6 ? lumMax / lumMin : Infinity;
  if (ratio < 1.3) return { hazeColor: mean, lightAngle: 180, lightWarmth: 0.2 };

  // A bright patch inside the frame means the sun is in front of us: backlit
  // birds. The further off centre, the more the light comes from the side,
  // where banking wings catch it; a patch at the frame edge implies a sun
  // just outside it, 70-100° off axis.
  const side = (cx - 0.5) * 2; // -1 left .. 1 right
  let angle = Math.sign(side) * lerp(15, 100, Math.pow(Math.abs(side), 0.7));
  if (angle < 0) angle += 360;

  const sum = mean[0] + mean[1] + mean[2];
  const warmth = sum > 0 ? Math.min(0.9, Math.max(0.2, ((mean[0] - mean[2]) / sum) * 3 + 0.3)) : 0.5;
  return { hazeColor: mean, lightAngle: angle, lightWarmth: warmth };
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
